import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  Button,
  Divider,
} from "@mui/material";
import useIsMobile from "components/hooks/useIsMobile";
import Breadcrumbs from "components/common/Breadcrumbs";
import { Download, ArrowUp, ArrowDown } from "lucide-react";
import Loading from "components/common/Loading";
import { useListInvoices } from "services/billing";
import { ListInvoicesRequest, Invoice } from "protogen/billing_service_pb";
import InvoiceFilters, {
  InvoiceFilter,
} from "components/payments/InvoiceFilters";
import { useSearchParams } from "react-router-dom";
import InvoiceCard from "components/payments/InvoiceCard";
import AutoSizer from "react-virtualized-auto-sizer";
import {
  VariableSizeList as List,
  ListChildComponentProps,
} from "react-window";

type SortFields = { field: string; asc: boolean }[];

type FilterView =
  | "pending"
  | "upcoming"
  | "paid"
  | "deferred"
  | "archived"
  | "approved"
  | "refunded"
  | "missing_bank"
  | "lightning"
  | "all";

const ONE_WEEK_SECONDS = 7 * 24 * 60 * 60;

const sortInvoicesByFields = (invoices: Invoice[], sortFields: SortFields) => {
  return invoices.sort((a, b) => {
    for (const sort of sortFields) {
      let valueA: string | number | bigint | boolean | undefined;
      let valueB: string | number | bigint | boolean | undefined;

      // Determine which field to sort by based on "name"
      if (sort.field === "status") {
        valueA = a.status;
        valueB = b.status;
      } else if (sort.field === "advisor-family") {
        valueA = `${a.advisor?.displayName.toLowerCase()}|${a.family?.name.toLowerCase()}`;
        valueB = `${b.advisor?.displayName.toLowerCase()}|${b.family?.name.toLowerCase()}`;
      } else if (sort.field === "familyName") {
        valueA = a.family?.name.toLowerCase();
        valueB = b.family?.name.toLowerCase();
      } else if (sort.field === "advisorName") {
        valueA = a.advisor?.displayName.toLowerCase();
        valueB = b.advisor?.displayName.toLowerCase();
      } else if (sort.field === "periodStartSec") {
        // Reversed since we want the most recent activity first.
        valueA = b.periodStartSec;
        valueB = a.periodStartSec;
      } else if (sort.field === "serviceMonth") {
        // Reversed since we want the most recent activity first.
        valueA = b.serviceMonth;
        valueB = a.serviceMonth;
      } else if (sort.field === "fee") {
        valueA = b.feePercent;
        valueB = a.feePercent;
      } else if (sort.field === "payment") {
        valueA = b.paymentCents;
        valueB = a.paymentCents;
      }

      if (valueA !== valueB) {
        if (valueA === undefined) return sort.asc ? -1 : 1;
        if (valueB === undefined) return sort.asc ? 1 : -1;

        if (valueA < valueB) return sort.asc ? -1 : 1;
        if (valueA > valueB) return sort.asc ? 1 : -1;
      }
    }
    return 0; // If all fields are equal
  });
};

const HeaderColumn = ({
  title,
  sortKey,
  sorts,
  setSorts,
  flex = "1",
  alignCenter = true,
}: {
  title: string;
  sortKey: string;
  sorts: SortFields;
  setSorts: (s: SortFields) => void;
  flex?: string;
  alignCenter?: boolean;
}) => {
  const arrowSx = {
    height: "20px",
    width: "20px",
    color: "#B3AFB6",
  };
  const currentSortVal = sorts.find((s) => s.field === sortKey);
  const upArrow = currentSortVal && !currentSortVal.asc;

  const onClick = () => {
    const newSorts = sorts.filter((s) => s.field !== sortKey);
    // First sort should always be ascending if not set yet.
    if (upArrow || !currentSortVal) {
      newSorts.unshift({ field: sortKey, asc: true });
    } else {
      newSorts.unshift({ field: sortKey, asc: false });
    }
    setSorts(newSorts);
  };

  return (
    <Box
      flex={flex}
      display="flex"
      alignItems={alignCenter ? "center" : undefined}
      gap="2px"
    >
      {upArrow ? <ArrowUp style={arrowSx} /> : <ArrowDown style={arrowSx} />}
      <Typography
        variant="bodySmallHeavy"
        color="#262626"
        onClick={onClick}
        sx={{
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

const Header = ({
  sorts,
  setSorts,
}: {
  sorts: SortFields;
  setSorts: (s: SortFields) => void;
}) => {
  return (
    <Box
      display="flex"
      flexDirection="row"
      justifyContent="space-between"
      alignItems="center"
      gap="12px"
      paddingBottom="12px"
    >
      <HeaderColumn
        setSorts={setSorts}
        sorts={sorts}
        title="Invoice"
        flex="5"
        // More natural to want to sort by invoice date here
        sortKey={"periodStartSec"}
        alignCenter={false}
      />
      <HeaderColumn
        setSorts={setSorts}
        sorts={sorts}
        title="Service Month"
        sortKey={"serviceMonth"}
        flex="2"
      />
      <HeaderColumn
        setSorts={setSorts}
        sorts={sorts}
        title="Fee %"
        sortKey={"fee"}
        flex="1"
      />
      <HeaderColumn
        setSorts={setSorts}
        sorts={sorts}
        title="Payment"
        sortKey={"payment"}
        flex="2"
      />
      <Box flex="2.5" />
      {/* <HeaderColumn
        setSorts={setSorts}
        sorts={sorts}
        title="Action"
        sortKey={"status"}
      /> */}
    </Box>
  );
};

const generateCsv = (invoices: Invoice[]) => {
  if (!invoices || !invoices.length) {
    return "No data";
  }
  // Example CSV headers:
  const headers = [
    "InvoiceRef",
    "AdvisorName",
    "FamilyName",
    "FeePercent",
    "PaymentCents",
    "Status",
    "StripeId",
    "PeriodStart",
    "PeriodEnd",
    "InvoiceAmountCents",
    "InvoiceCurrency",
    "ServiceMonth",
  ];
  // Convert each invoice to a CSV row
  const rows = invoices.map((i) => [
    i.ref,
    (i.advisor?.displayName || "").replace(/,/g, ""), // strip commas
    (i.family?.name || "").replace(/,/g, ""),
    (i.feePercent * 100).toString(),
    i.paymentCents.toString(),
    i.status,
    i.stripeId,
    i.periodStartSec?.toString() || "",
    i.periodEndSec?.toString() || "",
    i.invoiceAmountCents.toString(),
    i.invoiceCurrency,
    i.serviceMonth,
  ]);

  // Join together
  const csvContent = [
    headers.join(","), // header line
    ...rows.map((fields) => fields.join(",")),
  ].join("\n");
  return csvContent;
};

// ADDED: function to download a string as a file
const downloadFile = (content: string, filename: string) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const handleDownloadCsv = async (invoices: Invoice[]) => {
  // Generate CSV from the "all" invoices data
  const csvContent = generateCsv(invoices);
  // Copy to clipboard
  try {
    await navigator.clipboard.writeText(csvContent);
  } catch (e) {
    console.error("Clipboard copy failed: ", e);
  }
  // Then download as well
  downloadFile(csvContent, "invoices.csv");
};

type Props = {};
export default ({}: Props) => {
  const hasLoadedOnce = useRef(false);
  const isMobile = useIsMobile();
  const listRef = useRef<List>(null);
  const sizeMap = useRef<{ [key: string]: number }>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { request, loading } = useListInvoices();
  const [sorts, setSorts] = useState<SortFields>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterView, setFilterView] = useState<FilterView>(
    (new URLSearchParams(window.location.search).get("view") as FilterView) ||
      "pending",
  );
  const [filter, setFilter] = useState<InvoiceFilter>({
    createdAfterSeconds: null,
    useLastActionDate: false,
    advisorRef: "",
    familyRef: "",
  });
  const [actionsDisabled, setActionsDisabled] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [nextCursor, setNextCursor] = useState<string>("");
  const pageSize = 200;
  const [openedInvoiceRef, setOpenedInvoiceRef] = useState<string | null>(
    searchParams.get("invoice"),
  );
  const hasScrolledToInvoice = useRef(false);

  const setSize = (index: number, size: number) => {
    sizeMap.current[index] = size;
    listRef.current?.resetAfterIndex(index);
  };

  const getSize = (index: number) => {
    return sizeMap.current[index] || 181; // Default height if not set
  };

  const fetchInvoices = async (append: boolean = false) => {
    let statuses = undefined;
    let createdAfterSeconds = undefined;
    let includeExtraHours = true;
    let includeLightningTasks = true;
    let waitingPeriodBeforeSeconds = undefined;
    const pendingThreshold = Math.floor(Date.now() / 1000) - ONE_WEEK_SECONDS;

    switch (filterView) {
      case "pending":
        statuses = ["pending_approval"];
        waitingPeriodBeforeSeconds = pendingThreshold;
        break;
      case "upcoming":
        statuses = ["pending_approval"];
        createdAfterSeconds = pendingThreshold;
        includeExtraHours = false;
        break;
      case "deferred":
        statuses = ["deferred"];
        break;
      case "approved":
        statuses = ["approved"];
        break;
      case "paid":
        statuses = ["paid"];
        break;
      case "archived":
        statuses = ["archived"];
        break;
      case "refunded":
        statuses = ["payment_refunded"];
        break;
      case "missing_bank":
        statuses = ["pending_approval"];
        includeExtraHours = false;
      case "lightning":
        statuses = undefined;
        includeExtraHours = false;
        includeLightningTasks = true;
        break;
      case "all":
        statuses = undefined;
        break;
    }

    const response = await request(
      new ListInvoicesRequest({
        statuses,
        createdAfterSecStart: BigInt(
          Math.max(filter.createdAfterSeconds || 0, createdAfterSeconds || 0),
        ),
        ...(waitingPeriodBeforeSeconds && {
          waitingPeriodBeforeSec: BigInt(waitingPeriodBeforeSeconds),
        }),
        ...(filter.advisorRef && { advisorRef: filter.advisorRef }),
        ...(filter.familyRef && { familyRef: filter.familyRef }),
        includeExtraHours: includeExtraHours,
        useLastActionDate: filter.useLastActionDate,
        advisorMissingBank: filterView === "missing_bank",
        includeLightningTasks: includeLightningTasks,
        limit: pageSize,
        cursor: append ? nextCursor : "",
      }),
    );

    if (response) {
      setInvoices(
        append ? [...invoices, ...response.invoices] : response.invoices,
      );
      setNextCursor(response.nextCursor);
      hasLoadedOnce.current = true;
    }
  };

  useEffect(() => {
    setInvoices([]);
    setNextCursor("");
    hasLoadedOnce.current = false;
    fetchInvoices(false);
  }, [filter, filterView]);

  useEffect(() => {
    // Update URL whenever filterView changes
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (filterView !== "pending") {
        newParams.set("view", filterView);
      } else {
        newParams.delete("view");
      }
      return newParams;
    });
  }, [filterView, setSearchParams]);

  useEffect(() => {
    // Update URL when opened invoice changes
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (openedInvoiceRef) {
        newParams.set("invoice", openedInvoiceRef);
      } else {
        newParams.delete("invoice");
      }
      return newParams;
    });
  }, [openedInvoiceRef, setSearchParams]);

  const handleFilterViewChange = (newView: FilterView) => {
    setFilterView(newView);
  };

  const refresh = async () => {
    await fetchInvoices();
  };
  const resultingInvoices = sortInvoicesByFields(invoices, sorts);

  useEffect(() => {
    // Scroll to the opened invoice on initial load
    if (
      hasLoadedOnce.current &&
      openedInvoiceRef &&
      !hasScrolledToInvoice.current &&
      resultingInvoices.length > 0
    ) {
      const index = resultingInvoices.findIndex(
        (inv) => inv.ref === openedInvoiceRef,
      );
      if (index !== -1) {
        listRef.current?.scrollToItem(index, "start");
        hasScrolledToInvoice.current = true;
      }
    }
  }, [resultingInvoices, openedInvoiceRef]);

  const itemData = useMemo(
    () => ({
      invoices: resultingInvoices,
      refresh,
      filter,
      setFilter,
      setSize,
      actionsDisabled,
      setActionsDisabled,
      openedInvoiceRef,
      setOpenedInvoiceRef,
    }),
    [resultingInvoices, filter, actionsDisabled, openedInvoiceRef],
  );

  const Row = useCallback(({ index, style, data }: ListChildComponentProps) => {
    if (!data) return null;
    const {
      invoices,
      refresh,
      filter,
      setFilter,
      setSize,
      actionsDisabled,
      setActionsDisabled,
      openedInvoiceRef,
      setOpenedInvoiceRef,
    } = data;
    const invoice = invoices[index];
    return (
      <Box style={style}>
        <InvoiceCard
          key={invoice.ref}
          invoice={invoice}
          refresh={refresh}
          setAdvisorRef={(r) => setFilter({ ...filter, advisorRef: r })}
          setFamilyRef={(r) => setFilter({ ...filter, familyRef: r })}
          onHeightChange={(height) => setSize(index, height)}
          actionsDisabled={actionsDisabled}
          setActionsDisabled={setActionsDisabled}
          isExpanded={openedInvoiceRef === invoice.ref}
          onExpandedChange={(expanded) =>
            setOpenedInvoiceRef(expanded ? invoice.ref : null)
          }
        />
      </Box>
    );
  }, []);

  return (
    <Box
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px 20px 0px 20px" : "64px 64px 0 64px",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box display="flex" flexDirection="row">
        <Box
          display="flex"
          flexDirection="column"
          width="100%"
          marginBottom="16px"
        >
          <Breadcrumbs
            breadcrumbs={[
              { name: "Home", link: "/" },
              { name: "Tools", link: "/tools" },
            ]}
          />
          <Box
            display="flex"
            width="100%"
            gap={isMobile ? "12px" : "24px"}
            {...(isMobile && { flexDirection: "column" })}
            {...(!isMobile && {
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            })}
          >
            <Box
              display="flex"
              flexDirection="row"
              gap="12px"
              alignContent="center"
            >
              <Typography variant="display">Advisor Payments</Typography>
              <IconButton
                onClick={async () => {
                  await handleDownloadCsv(invoices);
                  setSnackbarOpen(true);
                }}
                disabled={!invoices.length}
              >
                <Download size={20} color="#B3AFB6" />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        gap="16px"
        justifyContent="space-between"
      >
        <InvoiceFilters
          setFilter={setFilter}
          filter={filter}
          loading={loading}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={filterView}
            onChange={(e) =>
              handleFilterViewChange(e.target.value as FilterView)
            }
            variant="outlined"
            sx={{
              minHeight: "48px",
              ".MuiSelect-select": {
                marginTop: "0",
              },
            }}
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="upcoming">Upcoming</MenuItem>
            <MenuItem value="deferred">Deferred</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="approved">Stuck (Approved + Unpaid)</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
            <MenuItem value="refunded">Refunded</MenuItem>
            <MenuItem value="missing_bank">Missing Bank Account</MenuItem>
            <MenuItem value="lightning">Lightning Tasks</MenuItem>
            <Divider />
            <MenuItem value="all">All</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          padding: !isMobile ? "16px 0 0" : undefined,
        }}
      >
        {!isMobile && <Header sorts={sorts} setSorts={setSorts} />}
        {loading && !hasLoadedOnce.current && <Loading />}
        <Box
          sx={{
            height: "100%",
            flex: "1 1 auto",
            ".hide-scrollbar::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <AutoSizer>
            {({ height, width }: { height: number; width: number }) => (
              <List
                ref={listRef}
                height={height}
                itemCount={resultingInvoices.length}
                itemSize={getSize}
                width={width}
                itemData={itemData}
                className="hide-scrollbar"
              >
                {Row}
              </List>
            )}
          </AutoSizer>
        </Box>
        {hasLoadedOnce.current && nextCursor && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              padding: "16px",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => fetchInvoices(true)}
              disabled={loading}
            >
              Load More
            </Button>
          </Box>
        )}
        {hasLoadedOnce.current && invoices.length === 0 && (
          <Typography variant="body" color="text.tertiary">
            No invoices found
          </Typography>
        )}
      </Box>
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={3000}
        message="CSV copied to clipboard!"
      />
    </Box>
  );
};
